/**
 * The email template editor's document format, and the ONE function that turns it into email HTML.
 *
 * WHY A DOCUMENT, NOT HTML: a company admin edits an email in a formatting editor (bold, lists, links,
 * variable chips). The editor sends this structured document — never HTML — and the server renders it
 * here, escaping every piece of text. There is no customer HTML to sanitise, so there is no way to get
 * `<script>`, an `onerror=` or a `javascript:` link into an email, however the request is crafted.
 * The web preview uses the same function, so what the admin sees is what is sent.
 *
 * Shape: a small, TipTap/ProseMirror-compatible subset. Anything outside it fails validation.
 * Pure and dependency-free (RN-safe, no Node built-ins), like the rest of @hrms/shared.
 */

import { z } from 'zod';

export const EMAIL_DOC_LIMITS = {
  /** Total characters of text across the document. */
  maxText: 20_000,
  maxBlocks: 200,
  maxSubject: 300,
  maxHref: 2_000,
} as const;

const variableName = z
  .string()
  .regex(/^[A-Za-z][A-Za-z0-9_]*$/)
  .max(64);

const markSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('bold') }),
  z.object({ type: z.literal('italic') }),
  z.object({ type: z.literal('underline') }),
  z.object({
    type: z.literal('link'),
    attrs: z.object({ href: z.string().trim().max(EMAIL_DOC_LIMITS.maxHref) }).passthrough(),
  }),
]);
export type EmailDocMark = z.infer<typeof markSchema>;

const inlineSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('text'),
    text: z.string().max(EMAIL_DOC_LIMITS.maxText),
    marks: z.array(markSchema).max(4).optional(),
  }),
  z.object({ type: z.literal('hardBreak') }),
  z.object({
    type: z.literal('variable'),
    attrs: z.object({ name: variableName }),
    // Bold/italic/underline only: a link on a variable could point anywhere the payload says.
    marks: z.array(markSchema).max(3).optional(),
  }),
]);
export type EmailDocInline = z.infer<typeof inlineSchema>;

const paragraphSchema = z.object({
  type: z.literal('paragraph'),
  content: z.array(inlineSchema).max(500).optional(),
});
const headingSchema = z.object({
  type: z.literal('heading'),
  attrs: z.object({ level: z.union([z.literal(2), z.literal(3)]) }),
  content: z.array(inlineSchema).max(500).optional(),
});
const listItemSchema = z.object({
  type: z.literal('listItem'),
  content: z.array(paragraphSchema).min(1).max(20),
});
const listSchema = z.object({
  type: z.enum(['bulletList', 'orderedList']),
  content: z.array(listItemSchema).min(1).max(100),
});

const blockSchema = z.union([paragraphSchema, headingSchema, listSchema]);
export type EmailDocBlock = z.infer<typeof blockSchema>;

export const emailDocSchema = z.object({
  type: z.literal('doc'),
  content: z.array(blockSchema).max(EMAIL_DOC_LIMITS.maxBlocks),
});
export type EmailDoc = z.infer<typeof emailDocSchema>;

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

const ENTITIES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
  // Braces too: typed text must never become a `{{placeholder}}` the engine would fill in.
  '{': '&#123;',
  '}': '&#125;',
};

function escapeText(value: string): string {
   
  return value
    // Control characters are stripped on purpose; this is the sanitizer, not a validator.
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
    .replace(/[&<>"'{}]/g, (c) => ENTITIES[c] ?? c);
}

/**
 * Only web and mail links; anything else (javascript:, data:, relative paths) is dropped.
 *
 * Deliberately does not use the `URL` global: this package ships RN-safe ESM with no Node/DOM
 * built-ins, and @hrms/payroll (which imports this barrel) sets `"types": []` to stay pure, so
 * neither environment is guaranteed to have `URL` typed or present. Validation and normalisation
 * are done with plain regexes/string ops instead, which every environment has.
 */
export function safeEmailHref(raw: string): string | null {
  const href = raw.trim();
  if (/^mailto:[^\s<>"']+$/i.test(href)) return href;
  const match = /^(https?:\/\/)([^/?#]*)([/?#]?.*)$/i.exec(href);
  if (!match || /[\s<>]/.test(href)) return null;
  // A bare authority with nothing (or only a query/fragment) after it gets a `/` inserted right
  // after the authority, matching what `new URL(href).toString()` used to give us for free.
  const [, scheme = '', authority = '', rest = ''] = match;
  const withPath = rest.startsWith('/') ? href : `${scheme}${authority}/${rest}`;
  // Percent-encode quotes so the href can't break out of the `href="..."` attribute it's embedded
  // in below — the same protection `new URL(href).toString()` gave for free.
  return withPath.replace(/"/g, '%22').replace(/'/g, '%27');
}

function applyMarks(html: string, marks: readonly EmailDocMark[] = [], allowLinks = true): string {
  let out = html;
  for (const mark of marks) {
    if (mark.type === 'bold') out = `<strong>${out}</strong>`;
    else if (mark.type === 'italic') out = `<em>${out}</em>`;
    else if (mark.type === 'underline') out = `<u>${out}</u>`;
    else if (allowLinks) {
      const href = safeEmailHref(mark.attrs.href);
      if (href) out = `<a href="${escapeText(href)}">${out}</a>`;
    }
  }
  return out;
}

function renderInline(nodes: readonly EmailDocInline[] = []): string {
  return nodes
    .map((node) => {
      if (node.type === 'hardBreak') return '<br />';
      if (node.type === 'variable') return applyMarks(`{{${node.attrs.name}}}`, node.marks, false);
      return applyMarks(escapeText(node.text), node.marks);
    })
    .join('');
}

/**
 * Document -> email HTML with `{{variable}}` placeholders left for the engine to fill (and escape).
 * A paragraph that holds nothing but an HTML-block variable (a task table) is left unwrapped, since a
 * table cannot sit inside a `<p>`.
 */
export function renderEmailDoc(doc: EmailDoc, htmlBlocks: readonly string[] = []): string {
  return doc.content
    .map((block) => {
      if (block.type === 'paragraph') {
        const only = block.content?.length === 1 ? block.content[0] : undefined;
        if (only?.type === 'variable' && htmlBlocks.includes(only.attrs.name)) {
          return `{{${only.attrs.name}}}`;
        }
        return `<p>${renderInline(block.content)}</p>`;
      }
      if (block.type === 'heading') {
        const tag = block.attrs.level === 2 ? 'h2' : 'h3';
        return `<${tag}>${renderInline(block.content)}</${tag}>`;
      }
      const tag = block.type === 'bulletList' ? 'ul' : 'ol';
      const items = block.content
        .map(
          (item) => `<li>${item.content.map((p) => renderInline(p.content)).join('<br />')}</li>`,
        )
        .join('');
      return `<${tag}>${items}</${tag}>`;
    })
    .join('\n');
}

/** Every variable the document and subject use. */
export function emailDocVariables(doc: EmailDoc, subject = ''): string[] {
  const names = new Set<string>();
  const visit = (nodes: readonly EmailDocInline[] = []) => {
    for (const n of nodes) if (n.type === 'variable') names.add(n.attrs.name);
  };
  for (const block of doc.content) {
    if (block.type === 'paragraph' || block.type === 'heading') visit(block.content);
    else for (const item of block.content) for (const p of item.content) visit(p.content);
  }
  for (const m of subject.matchAll(/\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g)) names.add(m[1]!);
  return [...names];
}

/** Total characters of typed text (the size limit). */
export function emailDocTextLength(doc: EmailDoc): number {
  let total = 0;
  const visit = (nodes: readonly EmailDocInline[] = []) => {
    for (const n of nodes) if (n.type === 'text') total += n.text.length;
  };
  for (const block of doc.content) {
    if (block.type === 'paragraph' || block.type === 'heading') visit(block.content);
    else for (const item of block.content) for (const p of item.content) visit(p.content);
  }
  return total;
}

// ---------------------------------------------------------------------------
// Built-in text -> document (so the editor can open the default wording)
// ---------------------------------------------------------------------------

/**
 * Convert a built-in template's `long` text (paragraphs separated by a blank line, `<strong>`/`<em>`
 * markup, `{{variables}}`) into a document. Only understands the markup the built-ins use.
 */
export function emailDocFromBuiltinText(long: string): EmailDoc {
  const paragraphs = long
    .split('\n\n')
    .map((p) => p.trim())
    .filter(Boolean);
  return {
    type: 'doc',
    content: paragraphs.map((p) => ({
      type: 'paragraph' as const,
      content: p.split('\n').flatMap((line, i) => {
        const inline = parseLine(line);
        return i === 0 ? inline : [{ type: 'hardBreak' as const }, ...inline];
      }),
    })),
  };
}

function parseLine(line: string): EmailDocInline[] {
  const out: EmailDocInline[] = [];
  const marks: EmailDocMark[] = [];
  const token = /(<\/?(?:strong|em)>|\{\{\s*[A-Za-z][A-Za-z0-9_]*\s*\}\})/g;
  let last = 0;
  const pushText = (text: string) => {
    if (!text) return;
    const decoded = text
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&');
    out.push(
      marks.length
        ? { type: 'text', text: decoded, marks: [...marks] }
        : { type: 'text', text: decoded },
    );
  };
  for (const m of line.matchAll(token)) {
    pushText(line.slice(last, m.index));
    last = (m.index ?? 0) + m[0].length;
    const t = m[0];
    if (t === '<strong>') marks.push({ type: 'bold' });
    else if (t === '<em>') marks.push({ type: 'italic' });
    else if (t === '</strong>' || t === '</em>') marks.pop();
    else {
      const name = t.replace(/[{}\s]/g, '');
      out.push(
        marks.length
          ? { type: 'variable', attrs: { name }, marks: [...marks] }
          : { type: 'variable', attrs: { name } },
      );
    }
  }
  pushText(line.slice(last));
  return out;
}
